/**
 * Everything Wrapped says, in one place.
 *
 * It used to be split between `compute.ts` (the seeded pools) and the slides
 * themselves (asides buried in ternaries inside JSX), which meant a third of
 * her weekly voice was invisible to the line dump and to the voice lints. Copy
 * belongs with copy: the report computes numbers, this file decides what to
 * say about them, and `WRAPPED_COPY` at the bottom is what the review dump
 * reads.
 *
 * These are screen lines, not toasts — the 160-character budget applies.
 */

import type { WrappedVerdict, WrappedWeek } from './compute.js'

export const VERDICT_LABEL: Record<WrappedVerdict, string> = {
  on: 'Tiksliai į dešimtuką',
  over: 'Savaitė pasidavė skoniui',
  under: 'Kukli savaitė',
  plain: 'Savaitė suskaičiuota',
}

export const SIGN_OFFS: readonly string[] = [
  'Savaitė užrašyta. Kita jau prasidėjo.',
  'Na. Tiek šitos savaitės.',
  'Uždarau. Rytoj atidarysiu naują.',
  'Savaitė tyliai virsta istorija. Kaip ir visos.',
  'Skaičiai sutikti, savaitė palaidota su pagarba.',
]

/**
 * Her paragraph about the week, chosen by how it went rather than written
 * about the figures — the numbers are on the other slides, and repeating them
 * here in words would be a report, not a commentary.
 */
export const COMMENTARY: Readonly<Record<WrappedVerdict, readonly string[]>> = {
  on: [
    'Septynios dienos, ir nė vienos, kurią reikėtų slėpti. Aš tokių savaičių mačiau nedaug.',
    'Tiksliai. Nuobodžiai tiksliai. Būtent tokios savaitės ir pakeičia žmogų.',
    'Nieko dramatiško. Tai ir yra pasiekimas — drama pigi, nuoseklumas brangus.',
    'Planas ir tikrovė sutapo. Retas įvykis. Įrašiau atskirai.',
    'Savaitė be nuotykių. Skaičiai patenkinti, aš — beveik.',
  ],
  over: [
    'Savaitė pasidavė skoniui. Būna. Nuo to ji nepasidaro blogesnė, tik storesnė.',
    'Viršyta. Neteisiu: aš irgi žinau, kaip atrodo penktadienis.',
    'Skaičiai kilo. Kita savaitė prasideda nuo nulio, ir tai vienintelė gera žinia šiandien.',
    'Daug. Bet įrašyta viskas, o tai svarbiau nei atrodo — nežinojimas kainuoja daugiau.',
    'Savaitė buvo dosni sau. Vieną kartą tai vadinasi poilsis, tris — tendencija.',
  ],
  under: [
    'Kukliau nei planuota. Trūkumas irgi turi kainą — ne visada tą, kurios tikiesi.',
    'Mažiau nei reikėjo. Aš skaičiuoju abi puses, ne tik viršų.',
    'Savaitė buvo santūri. Įsitikink, kad tai pasirinkimas, o ne užmaršumas.',
    'Po tikslu. Jei jautiesi gerai — puiku. Jei ne — skaičiai paaiškina kodėl.',
    'Kuklu. Kartais tai disciplina, kartais — praleisti pietūs. Skaičiai nesako, kuris.',
  ],
  plain: [
    'Savaitė suskaičiuota. Be tikslo palyginti nėra su kuo — bet įrašai jau yra pradžia.',
    'Duomenys surinkti, išvadų nėra. Nustatyk tikslą, ir kitą sekmadienį pakalbėsim rimčiau.',
    'Skaičiai yra, matas — ne. Aš mėgstu matus.',
    'Savaitė užfiksuota. Kol nėra tikslo, aš tik rašau. Ir man to maža.',
    'Įrašai yra. Prasmė ateis, kai bus su kuo lyginti.',
  ],
}

// ─── Slide asides ────────────────────────────────────────────────────────────
//
// One remark per slide, chosen by the figure rather than drawn at random: a
// report that praised a week of three logged days because the dice said so
// would be the one part of this she stopped believing.

export const DAYS_BOTH = 'Dvi dienos, tas pats žmogus. Įdomiausia dalis — kas jas skyrė.'
export const DAYS_ONE = 'Viena diena išsiskyrė, kitos laikėsi kartu. Retas nuoseklumas.'

export const NO_TOP_FOOD = 'Nieko nesikartojo dukart. Įvairi savaitė.'
export const NO_AWARDS = 'Nieko naujo trofėjų spintoje. Kita savaitė.'

export const PROTEIN_NO_TARGET =
  'Tikslo nėra, tad ir priekaištų nėra. Nustatyk jį — turėsiu ką pasakyti.'

export function proteinAside(week: WrappedWeek): string {
  const { daysMet } = week.protein
  if (daysMet >= week.daysLogged) return 'Nė vienos skylės. Net neturiu ką pridurti.'
  if (daysMet * 2 >= week.daysLogged) return 'Daugiau taip nei ne. Tai jau kryptis.'
  return 'Baltymai — vienintelis dalykas, kurio niekas nepersivalgo. Deja.'
}

// ─── Slide headlines ─────────────────────────────────────────────────────────
//
// The report is a sequence of slides now, and a slide's headline is the line
// that lands first and largest. They live here rather than in the JSX for the
// same reason the asides do: a headline in her voice is copy, and copy the
// dump cannot see is copy nobody reviews.

/** The kicker on the personal-lines chapter — skipped entirely when there is nothing to show. */
export const LEARNED_HEADING = 'Ką sužinojau apie tave šią savaitę'

export const NO_TOP_FOOD_TITLE = 'Nieko dukart'
export const PROTEIN_TITLE_CLEAN = 'Be spragų'
export const PROTEIN_TITLE = 'Dienos su tikslu'
export const NO_AWARDS_TITLE = 'Spinta nepasikeitė'
export const DAYS_NO_TARGET = 'Be tikslo dienų nėra su kuo lyginti. Bet jų forma vis tiek matosi.'

/** How many trophies landed this week, as a headline rather than a count. */
export function awardsTitle(count: number): string {
  return count === 1 ? 'Vienas naujas' : `${count} nauji`
}

/**
 * The last figure of the report, under the sign-off. It must not close the
 * week itself: one of the sign-offs above already says the next one has
 * started, and the two of them said it one under the other.
 */
export function totalLine(totalKcal: number): string {
  return `Iš viso per savaitę: ${totalKcal} kcal. Tiek jos ir buvo.`
}

export function streakAside(week: WrappedWeek): string {
  if (week.daysLogged === 7) return 'Septynios iš septynių. Nė vienos spragos.'
  if (week.longestRun >= 4) return 'Serija laikėsi ilgiau nei pusę savaitės. Tai ne atsitiktinumas.'
  return 'Spragų daugiau nei norėtųsi. Tarpai irgi kalba.'
}

/** The Sunday card on Today — she offers the report, she never opens it. */
export const OFFER_CARD = {
  title: 'Savaitė suvesta',
  body: 'Septynios dienos, vienas verdiktas. Aš jau perskaičiau. Dabar tu.',
  action: 'Skaityti',
}

/**
 * Every fixed string above, flattened for `scripts/dump-lines.ts`. Kept beside
 * the copy rather than in the script so a new slide cannot be added without
 * the review dump noticing.
 */
export const WRAPPED_COPY: Readonly<Record<string, readonly string[]>> = {
  'Verdict headlines': Object.values(VERDICT_LABEL),
  'Commentary — on target': COMMENTARY.on,
  'Commentary — over': COMMENTARY.over,
  'Commentary — under': COMMENTARY.under,
  'Commentary — no target': COMMENTARY.plain,
  'Best & worst day': [DAYS_BOTH, DAYS_ONE],
  Protein: [
    proteinAside({ daysLogged: 7, protein: { daysMet: 7 } } as WrappedWeek),
    proteinAside({ daysLogged: 7, protein: { daysMet: 4 } } as WrappedWeek),
    proteinAside({ daysLogged: 7, protein: { daysMet: 1 } } as WrappedWeek),
    PROTEIN_NO_TARGET,
  ],
  Consistency: [
    streakAside({ daysLogged: 7, longestRun: 7 } as WrappedWeek),
    streakAside({ daysLogged: 5, longestRun: 4 } as WrappedWeek),
    streakAside({ daysLogged: 3, longestRun: 2 } as WrappedWeek),
  ],
  'Nothing to report': [NO_TOP_FOOD, NO_AWARDS],
  'Learned heading': [LEARNED_HEADING],
  'Slide headlines': [
    NO_TOP_FOOD_TITLE,
    PROTEIN_TITLE_CLEAN,
    PROTEIN_TITLE,
    NO_AWARDS_TITLE,
    DAYS_NO_TARGET,
    awardsTitle(1),
    awardsTitle(2),
    totalLine(14_800),
  ],
  'Sign-offs': SIGN_OFFS,
  'Sunday card on Today': [OFFER_CARD.title, OFFER_CARD.body, OFFER_CARD.action],
}
